import { hasCookieConsent } from './cookieNotice';

export const MEASUREMENT_ID = 'G-NB77RKEC05';
export const ANALYTICS_COOKIE_PREFIX = 'flwrk_app';
const DISABLED = `ga-disable-${MEASUREMENT_ID}`;
const SCRIPT_ID = 'flwrk-google-analytics';
const DENIED = { analytics_storage: 'denied', ad_storage: 'denied', ad_user_data: 'denied', ad_personalization: 'denied' };
const PUBLIC_PAGES = {
  '/': 'FLWRK', '/features': 'Features', '/pricing': 'Pricing', '/about': 'About',
  '/privacy-policy': 'Privacy policy', '/terms-and-conditions': 'Terms and conditions',
  '/register': 'Register', '/login': 'Login',
};

// Explicit deployment opt-in after checking the shared stream's settings.
export function analyticsEnabled() {
  return process.env.REACT_APP_GA_ENABLED === 'true'
    && process.env.NODE_ENV === 'production'
    && window.location.hostname === 'app.flwrk.eu';
}

export function createAnalytics({ enabled = analyticsEnabled } = {}) {
  let initialized = false;
  let active = false;
  let lastPage = null;
  let script = null;
  const allowed = path => enabled() && hasCookieConsent('analytics')
    && Object.prototype.hasOwnProperty.call(PUBLIC_PAGES, path);
  const command = (...args) => window.gtag(...args);
  const pageData = path => ({
    page_location: `https://app.flwrk.eu${path}`,
    page_title: PUBLIC_PAGES[path],
    // Never forward authentication tokens, query strings or an arbitrary referrer.
    page_referrer: '',
  });

  function clearCookies() {
    document.cookie.split(';').forEach(part => {
      const name = part.trim().split('=')[0];
      if (!name.startsWith(`${ANALYTICS_COOKIE_PREFIX}_ga`)) return;
      ['', '; Domain=app.flwrk.eu'].forEach(domain => {
        document.cookie = `${name}=; Max-Age=0; Path=/${domain}; SameSite=Lax; Secure`;
      });
    });
  }

  function stop(removeCookies = true) {
    window[DISABLED] = true;
    if (initialized && active) command('consent', 'update', DENIED);
    // Clear pending commands too: a late script response must not replay a grant.
    if (initialized && window.dataLayer) window.dataLayer.length = 0;
    active = false;
    lastPage = null;
    if (removeCookies) clearCookies();
  }

  function sync(path) {
    if (!allowed(path)) {
      // A private route pauses measurement, but does not revoke an existing choice.
      stop(!enabled() || !hasCookieConsent('analytics'));
      return false;
    }
    if (!initialized) {
      // Do not stack a second tag on top of a separately injected integration.
      if (window.gtag || document.getElementById(SCRIPT_ID)
        || document.querySelector('script[src*="googletagmanager.com/"]')) return false;
      window.dataLayer = window.dataLayer || [];
      window.gtag = function () { window.dataLayer.push(arguments); };
      command('consent', 'default', DENIED);
      command('js', new Date());
      initialized = true;
    }
    window[DISABLED] = false;
    if (!active) command('consent', 'update', { ...DENIED, analytics_storage: 'granted' });
    active = true;
    command('config', MEASUREMENT_ID, {
      send_page_view: false,
      allow_google_signals: false,
      allow_ad_personalization_signals: false,
      cookie_domain: 'app.flwrk.eu',
      cookie_prefix: ANALYTICS_COOKIE_PREFIX,
      cookie_expires: 180 * 24 * 60 * 60,
      cookie_update: false,
      cookie_flags: 'SameSite=Lax;Secure',
      ...pageData(path),
    });
    if (!script) {
      script = document.createElement('script');
      script.id = SCRIPT_ID;
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`;
      script.referrerPolicy = 'origin';
      script.onerror = () => { stop(); script.remove(); script = null; };
      document.head.appendChild(script);
    }
    return true;
  }

  function page(path) {
    if (!sync(path) || lastPage === path) return;
    command('event', 'page_view', { send_to: MEASUREMENT_ID, ...pageData(path) });
    lastPage = path;
  }

  function event(name, path) {
    // Deliberately no arbitrary parameters: names/emails/form values cannot enter here.
    if (!['start_registration', 'sign_up'].includes(name)
      || (name === 'sign_up' && path !== '/register') || !sync(path)) return;
    command('event', name, {
      send_to: MEASUREMENT_ID, ...pageData(path),
      ...(name === 'sign_up' ? { method: 'email' } : {}),
    });
  }

  return { page, event, stop };
}

export const analytics = createAnalytics();
